import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, ArrowLeft, Check, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, NativeSelect, Empty } from "@/components/common";
import { fmtDT, toLocalInput } from "@/lib/format";
import { cn } from "@/lib/utils";

const NewInsp = ({ open, onOpenChange, onSaved }) => {
  const [cims, setCims] = useState([]);
  const [f, setF] = useState({ cimitero_id: "", title: "", scheduled_for: toLocalInput(Date.now() + 86400000) });
  useEffect(() => {
    if (open) api.get("/commesse").then((r) => setCims(r.data.flatMap((c) => c.cimiteri)));
  }, [open]);
  const save = async () => {
    try {
      await api.post("/ispezioni", { ...f, scheduled_for: new Date(f.scheduled_for).toISOString() });
      toast.success("Ispezione programmata");
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl" data-testid="insp-dialog">
        <DialogHeader><DialogTitle>Programma ispezione</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <NativeSelect className="w-full" testid="insp-form-cimitero" value={f.cimitero_id} onChange={(v) => setF({ ...f, cimitero_id: v })}><option value="">Cimitero…</option>{cims.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</NativeSelect>
          <div className="space-y-1"><Label>Titolo</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="insp-form-title" /></div>
          <div className="space-y-1"><Label>Data programmata</Label><Input type="datetime-local" value={f.scheduled_for} onChange={(e) => setF({ ...f, scheduled_for: e.target.value })} data-testid="insp-form-date" /></div>
        </div>
        <DialogFooter><Button disabled={!f.cimitero_id || f.title.length < 3} onClick={save} className="rounded-xl font-bold" data-testid="insp-form-submit">Programma</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function Ispezioni() {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => {
    api.get("/ispezioni").then((r) => setRows(r.data));
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  return (
    <div data-testid="ispezioni-page">
      <PageHeader title="Ispezioni" subtitle="Sopralluoghi per zone, collegati alle mappe"
        actions={["ceo", "responsabile"].includes(user.role) && <Button onClick={() => setOpen(true)} className="h-11 rounded-xl font-bold" data-testid="insp-new-button"><Plus className="mr-1 h-4 w-4" />Programma</Button>} />
      {rows?.length === 0 && <Empty text="Nessuna ispezione." />}
      <div className="grid gap-3 md:grid-cols-2">
        {rows?.map((i) => (
          <Link key={i.id} to={`/ispezioni/${i.id}`} className={cn("card-soft block p-4", i.is_overdue && "overdue-row")} data-testid={`insp-card-${i.code}`}>
            <div className="flex items-center gap-2"><span className="font-mono text-xs">{i.code}</span><span className="rounded-full bg-muted px-2 text-xs font-semibold">{i.status}</span>{i.is_overdue && <span className="rounded-full bg-red-600 px-2 text-xs font-bold text-white">non eseguita</span>}</div>
            <p className="mt-1 font-bold">{i.title}</p>
            <p className="text-xs text-muted-foreground">{i.cimitero_name} · {fmtDT(i.scheduled_for)} · {i.inspector_name} · {i.items.length} esiti, {i.anomalies} anomalie</p>
          </Link>
        ))}
      </div>
      <NewInsp open={open} onOpenChange={setOpen} onSaved={load} />
    </div>
  );
}

export function IspezioneDetail() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [area, setArea] = useState("");
  const [note, setNote] = useState("");
  const [sev, setSev] = useState("media");
  const load = useCallback(() => {
    api.get(`/ispezioni/${id}`).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [id]);
  useEffect(() => {
    load();
  }, [load]);
  const esito = async (category, value) => {
    try {
      const { data } = await api.post(`/ispezioni/${id}/esiti`, { area_id: area, category, esito: value, note, severity: sev });
      setD(data);
      setNote("");
      toast.success(value === "ok" ? "Esito OK registrato" : "Anomalia registrata: criticità creata");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const complete = async () => {
    try {
      setD((await api.post(`/ispezioni/${id}/complete`)).data);
      toast.success("Ispezione completata");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  if (!d) return <p className="text-muted-foreground">Caricamento…</p>;
  return (
    <div data-testid="ispezione-detail-page" className="fade-up">
      <Link to="/ispezioni" className="mb-4 inline-flex items-center gap-1 text-sm font-semibold text-muted-foreground"><ArrowLeft className="h-4 w-4" />Ispezioni</Link>
      <PageHeader title={d.title} subtitle={`${d.code} · ${d.cimitero_name} · ${fmtDT(d.scheduled_for)} · ${d.status}`}
        actions={d.can_edit && <Button onClick={complete} className="h-11 rounded-xl font-bold" data-testid="insp-complete-button"><Check className="mr-1 h-4 w-4" />Completa ispezione</Button>} />
      {d.can_edit && (
        <div className="card-soft mb-5 space-y-3 p-5" data-testid="insp-form">
          <NativeSelect className="w-full" testid="insp-area-select" value={area} onChange={setArea}><option value="">Seleziona la zona…</option>{d.areas.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}</NativeSelect>
          <div className="flex gap-2"><Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Nota (obbligatoria per le anomalie)" data-testid="insp-note-input" />
            <NativeSelect testid="insp-severity" value={sev} onChange={setSev}>{["bassa", "media", "alta", "emergenza"].map((s) => <option key={s} value={s}>{s}</option>)}</NativeSelect></div>
          <div className="grid gap-2 sm:grid-cols-2">
            {d.categories.map((c) => (
              <div key={c} className="flex items-center gap-2 rounded-xl border p-2">
                <span className="flex-1 text-sm font-semibold capitalize">{c}</span>
                <Button size="sm" disabled={!area} onClick={() => esito(c, "ok")} className="min-h-[44px] rounded-xl bg-emerald-600 hover:bg-emerald-700" data-testid={`insp-ok-${c}`}>OK</Button>
                <Button size="sm" disabled={!area} variant="destructive" onClick={() => esito(c, "anomalia")} className="min-h-[44px] rounded-xl" data-testid={`insp-ko-${c}`}><AlertTriangle className="mr-1 h-3 w-3" />Anomalia</Button>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="card-soft p-5" data-testid="insp-results"><h3 className="mb-3 font-bold">Esiti registrati ({d.items.length})</h3>
        {d.items.map((it) => (
          <p key={it.id} className="border-b py-2 text-sm last:border-0"><b>{it.area_label}</b> · {it.category} · <b className={it.esito === "ok" ? "text-emerald-700" : "text-red-600"}>{it.esito}</b> {it.note}
            {it.criticita_id && <Link to={`/criticita/${it.criticita_id}`} className="ml-2 font-semibold text-primary">{it.criticita_code}</Link>}<span className="ml-2 text-xs text-muted-foreground">{it.by_name} · {fmtDT(it.at)}</span></p>
        ))}
      </div>
    </div>
  );
}
